<script>
import { defineComponent as _defineComponent } from "vue";
import { unref as _unref, renderSlot as _renderSlot, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = {
  viewBox: "0 0 100 100",
  xmlns: "http://www.w3.org/2000/svg"
};
const _hoisted_2 = ["points"];
import {
  ref,
  computed,
  inject,
  toValue
} from "vue";
import {
  useFloating,
  autoUpdate,
  flip,
  shift,
  limitShift,
  arrow as floatingArrow
} from "@floating-ui/vue";
import { MagicMenuInstanceId, MagicMenuViewId } from "../symbols";
import { useMenuView } from "../composables/private/useMenuView";
import { useMenuState } from "../composables/private/useMenuState";
import { ModeFloatingStrategy } from "../utils/modeFloatingStrategyDefaults";
export default /* @__PURE__ */ _defineComponent({
  __name: "MagicMenuFloat",
  props: {
    placement: { type: null, required: false },
    middleware: { type: Array, required: false },
    arrow: { type: Boolean, required: false },
    referenceEl: { type: null, required: false }
  },
  setup(__props) {
    const elRef = ref(void 0);
    const arrowRef = ref(void 0);
    const instanceId = inject(MagicMenuInstanceId, void 0);
    const viewId = inject(MagicMenuViewId, void 0);
    const { initializeState } = useMenuState(instanceId ?? "");
    const state = initializeState();
    const { getView } = useMenuView(instanceId ?? "");
    const view = getView(viewId ?? "");
    const mappedPlacement = computed(() => {
      if (__props.placement) {
        return __props.placement;
      }
      switch (state.options.mode) {
        case "menubar":
          return !view?.parent.item ? "bottom-start" : "right-start";
        case "dropdown":
          return !view?.parent.item ? "bottom" : "right-start";
        case "context":
          return "right-start";
        default:
          return void 0;
      }
    });
    const hasArrow = computed(
      () => __props.arrow ?? (state.options.mode === "dropdown" && !view?.parent.item)
    );
    const mappedMiddleware = computed(() => {
      const combined = [];
      if (__props.middleware) {
        combined.push(...__props.middleware);
      }
      switch (state.options.mode) {
        case "menubar":
          if (!view?.parent.item) {
            combined.push(
              flip({
                crossAxis: true
              })
            );
          } else if (!!view?.parent.item) {
            combined.push(
              flip({
                crossAxis: false
              })
            );
            combined.push(
              shift({
                crossAxis: true,
                limiter: limitShift()
              })
            );
          }
          break;
        case "dropdown":
          combined.push(
            flip({
              mainAxis: true,
              crossAxis: false
            })
          );
          combined.push(
            shift({
              mainAxis: true,
              crossAxis: false,
              limiter: limitShift()
            })
          );
          if (hasArrow.value) {
            combined.push(
              floatingArrow({
                element: arrowRef.value
              })
            );
          }
          break;
        case "context":
          combined.push(
            flip({
              mainAxis: true,
              crossAxis: true
            })
          );
          break;
      }
      return combined;
    });
    const mappedReferenceEl = computed(() => {
      if (__props.referenceEl) {
        return toValue(__props.referenceEl);
      } else if (view?.state.clicked) {
        return {
          getBoundingClientRect() {
            return {
              width: 0,
              height: 0,
              x: view.state.clicked.x,
              y: view.state.clicked.y,
              top: view.state.clicked.y,
              left: view.state.clicked.x,
              right: view.state.clicked.x,
              bottom: view.state.clicked.y
            };
          }
        };
      } else {
        return document.querySelector(`[data-id="${viewId}-trigger"]`);
      }
    });
    const mappedStrategy = computed(() => {
      return state.options.floating?.strategy ?? ModeFloatingStrategy[state.options.mode];
    });
    const {
      floatingStyles,
      placement: floatingPlacement,
      middlewareData
    } = useFloating(mappedReferenceEl, elRef, {
      placement: mappedPlacement,
      strategy: mappedStrategy,
      whileElementsMounted: autoUpdate,
      middleware: mappedMiddleware
    });
    const arrowStyles = computed(() => {
      if (!hasArrow.value) {
        return {};
      }
      const translate = `translate3d(${middlewareData.value.arrow?.x ? `${middlewareData.value.arrow.x}px` : "0"}, ${middlewareData.value.arrow?.y ? `${middlewareData.value.arrow.y}px` : "0"}, 0)`;
      return {
        transform: translate
      };
    });
    const placementClasses = computed(() => {
      return floatingPlacement.value.split("-").map((value) => `-${value}`).join(" ");
    });
    const polygonPoints = computed(() => {
      const position = floatingPlacement.value.split("-")[0];
      switch (position) {
        case "bottom":
          return "50,50 100,100 0,100";
        case "top":
          return "50,50 100,0 0,0";
        case "right":
          return "50,50 100,100 100,0";
        case "left":
          return "50,50 0,100 0,0";
        default:
          return void 0;
      }
    });
    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock(
        "div",
        {
          ref_key: "elRef",
          ref: elRef,
          class: _normalizeClass(["magic-menu-float", placementClasses.value]),
          style: _normalizeStyle(_unref(floatingStyles))
        },
        [
          hasArrow.value ? (_openBlock(), _createElementBlock(
            "div",
            {
              key: 0,
              ref_key: "arrowRef",
              ref: arrowRef,
              class: "magic-menu-float__arrow",
              style: _normalizeStyle(arrowStyles.value)
            },
            [
              _renderSlot(_ctx.$slots, "arrow", {}, () => [
                (_openBlock(), _createElementBlock("svg", _hoisted_1, [
                  _createElementVNode("polygon", {
                    points: polygonPoints.value,
                    fill: "currentColor"
                  }, null, 8, _hoisted_2)
                ]))
              ])
            ],
            4
            /* STYLE */
          )) : _createCommentVNode("v-if", true),
          _renderSlot(_ctx.$slots, "default")
        ],
        6
        /* CLASS, STYLE */
      );
    };
  }
});
</script>

<style>
.magic-menu-float {
  display: flex;
  z-index: var(--magic-menu-float-z-index, 999);
}

.magic-menu-float.-top {
  flex-direction: column-reverse;
}

.magic-menu-float.-bottom {
  flex-direction: column;
}

.magic-menu-float.-left {
  flex-direction: row-reverse;
}

.magic-menu-float.-right {
  flex-direction: row;
}

.magic-menu-float__arrow {
  width: var(--magic-menu-float-arrow-width, 0.75rem);
  height: var(--magic-menu-float-arrow-height, 0.75rem);
  color: var(--magic-menu-float-arrow-color, inherit);
  & svg {
    width: 100%;
    height: 100%;
  }
}
</style>
